import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Button, Stack, Typography, } from '@mui/material';
import { Auth } from 'aws-amplify';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { navStore, passwordValidator, useStore, useTokens, } from '@mcn-platform/models';
import capitalizeFirstLetter from '../common/capitalizeFirstLetter';
import AuthDialog from './AuthDialog';
import TextField from '../form/TextField';
import PasswordField from '../form/PasswordField';
import PhoneField from '../form/PhoneField';
import SubmitButton from '../form/SubmitButton';
import FormStack from '../form/FormStack';
const buildSignUpValidator = (tokens, additionalFields, requiredFields) => {
    // console.log('sign up fields', { additionalFields, requiredFields });
    const signUpValidator = Yup.object().shape({
        newEmail: Yup.string().email('Please enter a valid email address.').required('Email address is required.'),
        newPassword: passwordValidator(),
        newPasswordConfirm: Yup.string().oneOf([Yup.ref('newPassword'), null], 'Passwords must match.').required('Confirm Password is required.'),
        confirmationCode: Yup.string().when('step', {
            is: 'CONFIRM',
            then: Yup.string().required('Confirmation code is required.'),
        }),
        confirm: Yup.boolean(),
        ...additionalFields.reduce((acc, field) => {
            acc[field] = requiredFields.includes(field)
                ? Yup.string().required(`${tokens.getToken(`customer${capitalizeFirstLetter(field)}Label`)} is required.`)
                : Yup.string();
            return acc;
        }, {}),
    });
    // console.log('validator', signUpValidator);
    return signUpValidator;
};
const SignUp = ({ display, handleClose, showSignIn, }) => {
    const store = useStore();
    const tokens = useTokens();
    const organization = store.currentOrganization;
    const additionalFields = organization.signUpFields || [];
    const requiredFields = organization.signUpRequiredFields || [];
    const [state, setState] = useState('START');
    const [message, setMessage] = useState();
    const { control, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors, isValid }, getValues, handleSubmit, reset, setValue, } = useForm({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        defaultValues: {
            step: 'START',
            newEmail: '',
            newPassword: '',
            newPasswordConfirm: '',
            confirmationCode: '',
            ...additionalFields.reduce((acc, field) => {
                acc[field] = '';
                return acc;
            }, {}),
        },
        // @ts-expect-error mismatch between defaultValues and resolver
        resolver: yupResolver(buildSignUpValidator(tokens, additionalFields, requiredFields)),
    });
    // console.log('errors', errors);
    if (display === 'HIDDEN') {
        return null;
    }
    const setStep = (step) => {
        setState(step);
        setValue('step', step);
    };
    const handleSignUp = async ({ newEmail, newPassword, newPasswordConfirm, ...fieldValues }) => {
        try {
            navStore.showBlockInput();
            const results = await Auth.signUp({
                username: newEmail,
                password: newPassword,
                attributes: {
                    email: newEmail,
                    ...(fieldValues.phoneNumber ? { phone_number: fieldValues.phoneNumber } : {}),
                },
            });
            console.log('results', results);
            console.log('saving customer attributes');
            await store.setCustomerAttributes(
            // @ts-expect-error pool does exist on user
            `${results.user.pool.userPoolId}_${results.userSub}`, ...[additionalFields.map((field) => ({
                    name: field,
                    value: fieldValues[field],
                }))]);
            setMessage('You have been sent a confirmation code to your email address.  Please confirm your account.');
            setStep('CONFIRM');
        }
        catch (exception) {
            console.log('error on sign-up', exception);
            setMessage(exception.message);
            setStep('START');
        }
        finally {
            navStore.clearBlockInput();
        }
    };
    const handleConfirm = async ({ newEmail, confirmationCode, newPassword }) => {
        try {
            navStore.showBlockInput();
            const results = await Auth.confirmSignUp(newEmail, confirmationCode);
            console.log('results', results);
            const user = await Auth.signIn({ username: newEmail, password: newPassword });
            console.log('Auto sign-in user', user);
            reset();
            setMessage('');
            setStep('SUCCESS');
            handleClose();
        }
        catch (exception) {
            console.log('error signing up', exception);
            setMessage(exception.message);
            setStep('CONFIRM');
        }
        finally {
            navStore.clearBlockInput();
        }
    };
    const handleResendCode = async ({ newEmail }) => {
        try {
            navStore.showBlockInput();
            const results = await Auth.resendSignUp(newEmail);
            console.log('results', results);
            setMessage('A new confirmation code has been sent.');
            setStep('CONFIRM');
        }
        catch (exception) {
            console.log('error signing up', exception);
            setMessage(exception.message);
            setStep('CONFIRM');
        }
        finally {
            navStore.clearBlockInput();
        }
    };
    let fields;
    let actions;
    let submit;
    if (state === 'START') {
        fields = (_jsxs(FormStack, { children: [message && (_jsx(Typography, { variant: "h6", children: message })), _jsx(TextField, { label: "Email", name: "newEmail", control: control, required: true, autocomplete: "off" }, "newEmail"), _jsx(PasswordField, { label: "Password", name: "newPassword", control: control, required: true, autocomplete: "off" }, "newPassword"), _jsx(PasswordField, { label: "Confirm Password", name: "newPasswordConfirm", control: control, required: true, autocomplete: "off" }, "newPasswordConfirm"), additionalFields.map((field) => {
                    if (field === 'phoneNumber') {
                        return (_jsx(PhoneField, { label: tokens.getToken(`customer${capitalizeFirstLetter(field)}Label`), name: field, control: control, required: requiredFields.includes(field) }, field));
                    }
                    return (_jsx(TextField, { label: tokens.getToken(`customer${capitalizeFirstLetter(field)}Label`), name: field, control: control, required: requiredFields.includes(field) }, field));
                })] }));
        actions = (_jsx(SubmitButton, { disabled: !isValid, label: "Create Account" }));
        submit = handleSubmit(handleSignUp);
    }
    else if (state === 'CONFIRM') {
        fields = (_jsxs(FormStack, { children: [message && (_jsx(Typography, { variant: "h6", children: message })), _jsx(TextField, { label: "Confirmation Code", name: "confirmationCode", control: control, required: true, autocomplete: "one-time-code" }, "confirmationCode")] }));
        actions = (_jsxs(Stack, { direction: "column", spacing: 2, width: 1, children: [_jsx(SubmitButton, { disabled: !isValid, label: "Confirm" }), _jsx(Button, { fullWidth: true, onClick: () => handleResendCode(getValues()), className: "site-system-button-secondary", children: "Resend Code" })] }));
        submit = handleSubmit(handleConfirm);
    }
    else if (state === 'SUCCESS') {
        fields = (_jsx(Typography, { variant: "body1", children: "You have created your account. Please sign in." }));
        actions = (_jsx(Stack, { direction: "row", sx: { m: 4 }, width: 1, children: _jsx(Button, { fullWidth: true, onClick: showSignIn, className: "site-system-button-primary", children: "Sign In" }) }));
        submit = () => { };
    }
    else {
        fields = (_jsx(Typography, { variant: "body1", children: "There was an error signing in. Please contact your administrator." }));
        actions = null;
    }
    const extraActions = (_jsx(Stack, { direction: "row", width: 1 }));
    // console.log('getValues', getValues());
    if (display === 'INLINE') {
        return (_jsx("form", { onSubmit: submit, noValidate: true, children: _jsxs(FormStack, { children: [fields, actions, extraActions] }) }));
    }
    return (_jsx(AuthDialog, { open: display === 'DIALOG', handleSubmit: submit, handleClose: handleClose, title: "Sign Up", formFields: fields, formActions: actions, extraActions: extraActions }));
};
export default SignUp;
//# sourceMappingURL=SignUp.js.map